import { Link as RLink } from 'react-router-dom';
import { AttendancePage as ClassRegister } from '@/features/owner/pages/AttendancePage';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { call } from '@/api/desk';
import { DataTable, DateInput, Filters, FormModal, Notice, PageHeader, Sel, Stat, StudentPicker, Tabs, errMsg, isNA, today, useAction, useGet, inputCls, studentName } from '@/features/shared/ops/kit';
import { badge, useClassOptions, useStaffOptions } from '../lib';

const STATUS = ['present', 'absent', 'late', 'excused'];
const monthStart = () => today().slice(0, 8) + '01';

function Report() {
  const { options: classOpts } = useClassOptions();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [classId, setClassId] = useState('');
  const [student, setStudent] = useState<any>(null);
  const q = useGet<any>('desk-att-report', '/school/attendance/report', { from, to, class_id: student ? '' : classId, student_id: student?.id });
  const d = q.data;
  const cols = [
    { head: 'Present', cell: (r: any) => r.present },
    { head: 'Late', cell: (r: any) => r.late },
    { head: 'Absent', cell: (r: any) => r.absent },
    { head: 'Excused', cell: (r: any) => r.excused },
    { head: 'Rate', cell: (r: any) => (r.rate == null ? '—' : `${r.rate}%`) },
  ];
  return (
    <Card title="Attendance report">
      <Filters>
        <DateInput label="From" value={from} onChange={setFrom} />
        <DateInput label="To" value={to} onChange={setTo} />
        <Sel value={classId} onChange={setClassId} options={classOpts} placeholder="All classes" />
        <div className="w-64"><StudentPicker label="One student (optional)" value={student} onPick={setStudent} /></div>
      </Filters>
      {q.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {q.error && <Notice kind="error">{errMsg(q.error)}</Notice>}
      {d?.classes && <DataTable rows={d.classes} cols={[{ head: 'Class', cell: (r: any) => r.class }, ...cols]} />}
      {d?.students && <DataTable rows={d.students} empty="No students." cols={[{ head: 'Student', cell: (r: any) => r.name }, ...cols]} />}
      {d?.days_list && (
        <>
          <p className="text-sm mb-2"><b>{d.student}</b>: {d.present} present, {d.late} late, {d.absent} absent, {d.excused} excused{d.rate != null && ` · ${d.rate}%`}</p>
          <DataTable rows={d.days_list} empty="No records in this range." cols={[{ head: 'Date', cell: (r: any) => r.date }, { head: 'Status', cell: (r: any) => badge(r.status) }, { head: 'Note', cell: (r: any) => r.note ?? '' }]} />
        </>
      )}
    </Card>
  );
}

export function StaffAttendanceCard() {
  const qc = useQueryClient();
  const { options: staffOpts } = useStaffOptions();
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [userId, setUserId] = useState('');
  const q = useGet<any>('desk-staff-att', '/school/staff-attendance', { from, to, user_id: userId });
  const [adding, setAdding] = useState(false);
  const rows: any[] = q.data?.rows ?? [];
  const sum = q.data?.summary;
  return (
    <Card title="Staff attendance" action={<Button onClick={() => setAdding(true)}>Record manually</Button>}>
      <Filters>
        <DateInput label="From" value={from} onChange={setFrom} />
        <DateInput label="To" value={to} onChange={setTo} />
        <Sel value={userId} onChange={setUserId} options={staffOpts} placeholder="All staff" />
      </Filters>
      {sum && <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">{Object.entries(sum).map(([k, v]) => <Stat key={k} label={k.replace(/_/g, ' ')} value={v as number} />)}</div>}
      <DataTable loading={q.isLoading} error={q.error} rows={rows} empty="No staff clock-ins in this range." cols={[
        { head: 'Staff', cell: (r) => r.user ?? '—' },
        { head: 'Date', cell: (r) => r.date ?? '—' },
        { head: 'In', cell: (r) => r.clock_in_at ?? r.clock_in ?? '—' },
        { head: 'Out', cell: (r) => r.clock_out_at ?? r.clock_out ?? '—' },
        { head: 'Status', cell: (r) => badge(r.status) },
        { head: 'Late (min)', cell: (r) => r.minutes_late ?? '' },
      ]} />
      {adding && <ManualClockForm url="/school/staff-attendance/manual" onClose={() => setAdding(false)} onDone={() => qc.invalidateQueries({ queryKey: ['desk-staff-att'] })} />}
    </Card>
  );
}

export function ManualClockForm({ url, onClose, onDone }: { url: string; onClose: () => void; onDone: () => void }) {
  const { options: staffOpts } = useStaffOptions();
  return (
    <FormModal title="Record staff attendance" onClose={onClose} onSubmit={async (v) => { await call('post', url, { body: v }); onDone(); }}
      initial={{ date: today(), status: 'on_time' }}
      fields={[
        { name: 'user_id', label: 'Staff member', type: 'select', required: true, options: staffOpts },
        { name: 'date', label: 'Date', type: 'date', required: true },
        { name: 'status', label: 'Status', type: 'select', required: true, options: [{ value: 'on_time', label: 'On time' }, { value: 'late', label: 'Late' }, { value: 'absent', label: 'Absent' }, { value: 'excused', label: 'Excused' }] },
        { name: 'clock_in', label: 'Clock in', type: 'time', required: true, show: (v) => v.status === 'on_time' || v.status === 'late' },
        { name: 'clock_out', label: 'Clock out', type: 'time' , show: (v) => v.status === 'on_time' || v.status === 'late' },
        { name: 'note', label: 'Reason / note', type: 'textarea', required: true, maxLength: 200 },
      ]} />
  );
}

export function AttendancePage() {
  const [tab, setTab] = useState<'register' | 'report'>('register');
  return (
    <div>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-5 flex flex-wrap items-center gap-2">
        <div className="flex gap-1 bg-slate-100 rounded-2xl p-1">
          {([['register', 'Class register'], ['report', 'Reports']] as const).map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-xl text-sm font-semibold ${tab === k ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>{l}</button>)}
        </div>
        <RLink to="/desk/staff-attendance" className="ml-auto text-sm font-semibold text-[#0A2540] underline">Staff attendance & QR events →</RLink>
      </div>
      {tab === 'register' ? <ClassRegister /> : <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5"><Report /></div>}
    </div>
  );
}
